/**
 * Iter 138 — Global Huddle Join Widget.
 *
 * Behavior:
 *   - Polls `/workspaces/{ws}/my-active-huddles` every 45s.
 *   - When any active huddle needs a response:
 *     - Renders as a NON-DISMISSABLE full-screen modal for the first 3 minutes
 *       (so the user *can't* ignore a huddle they should be in).
 *     - After 3 min, auto-collapses into a resizable/draggable bottom-right
 *       floating card. The card CANNOT be closed — only submitted.
 *   - Clicking the "Answer now" button opens the shared RespondDialog with
 *     the widget's prompt list; on successful submit, the row disappears
 *     from `my-active-huddles` on the next poll and the widget hides.
 *
 * Kept intentionally self-contained: no route knowledge, single global
 * mount inside AppShell so it's present on every authenticated page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as React from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Radio, ChevronUp, ChevronDown, Clock, SkipForward } from "lucide-react";
import HuddleRespondDialog from "@/components/HuddleRespondDialog";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

const POLL_MS = 15_000;  // Feb 2026 · Cut from 45s → 15s so a user who
                         // just finished check-in sees the huddle popup
                         // land near-instantly instead of waiting ~45s.
const MODAL_LOCK_MS = 3 * 60_000;  // 3 minutes.
// Iter 253z-14 · Snooze-all key lives outside per-huddle dismissals so a
// fresh huddle popping mid-snooze still stays quiet.
const SNOOZE_ALL_KEY = "acq_huddle_snooze_all_until";
const SNOOZE_MS = 10 * 60_000;  // 10 minutes.

export default function HuddleJoinWidget() {
  const { user, activeWorkspaceId } = useAuth();
  const [active, setActive] = useState([]);
  const [respondingInst, setRespondingInst] = useState(null);
  // Iter 138b — persist "Not now" across route changes via localStorage
  // keyed by `${wsId}:${instance_id}`. Cleared naturally when the 3-min
  // lock window elapses (we still gate on `inLockWindow`) OR when the user
  // submits and the row leaves `my-active-huddles`.
  const dismissKey = (iid) => `acq_huddle_dismissed:${activeWorkspaceId}:${iid}`;
  const [dismissedModalIds, setDismissedModalIds] = useState(() => new Set());
  const [bodyCollapsed, setBodyCollapsed] = useState(false);
  // Feb 2026 · Set of instance_ids we've already auto-opened in this
  // tab/session so a user who closes the Respond dialog isn't
  // auto-popped again every poll tick.
  const autoOpenedRef = useRef(new Set());
  // Feb 2026 · Suppress the whole widget while policies / mandatory
  // check-in are pending so stacked modals don't collide. Values are
  // refreshed in parallel with the active-huddles poll.
  const [blockedByPolicy, setBlockedByPolicy] = useState(false);
  const [blockedByCheckin, setBlockedByCheckin] = useState(false);
  const firstSeenAt = useRef({});
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    if (!activeWorkspaceId || !user) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/my-active-huddles`);
      setActive(data || []);
      const now = Date.now();
      // Merge localStorage dismissals so a hard nav preserves "Not now".
      const rehydrated = new Set(dismissedModalIds);
      (data || []).forEach((row) => {
        if (!firstSeenAt.current[row.instance_id]) {
          firstSeenAt.current[row.instance_id] = now;
        }
        if (localStorage.getItem(dismissKey(row.instance_id)) !== null) {
          rehydrated.add(row.instance_id);
        }
      });
      if (rehydrated.size !== dismissedModalIds.size) setDismissedModalIds(rehydrated);
    } catch { /* silent — background poll */ }
    // Feb 2026 · Resolve blockers so we can defer the widget until the
    // user has cleared policies + check-in. These calls are cheap and
    // already memoized at the server, so polling them with the huddle
    // list keeps the state coherent without extra lifecycle.
    try {
      const { data: p } = await api.get(`/workspaces/${activeWorkspaceId}/policies/my-pending`);
      setBlockedByPolicy(!!(p?.gate_enabled && (p?.pending || []).length > 0));
    } catch { setBlockedByPolicy(false); }
    try {
      const [{ data: st }, { data: t }] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/hr-settings`).catch(() => ({ data: {} })),
        api.get(`/workspaces/${activeWorkspaceId}/attendance/today`).catch(() => ({ data: null })),
      ]);
      if (!st?.force_checkin) { setBlockedByCheckin(false); }
      else {
        const clockedIn = !!t?.open_punch;
        const isOff = !!t?.holiday || !!t?.weekly_off || t?.record?.source === "leave";
        setBlockedByCheckin(!clockedIn && !isOff);
      }
    } catch { setBlockedByCheckin(false); }
  }, [activeWorkspaceId, user, dismissedModalIds]);

  const dismissModal = (iid) => {
    setDismissedModalIds((prev) => new Set([...prev, iid]));
    try { localStorage.setItem(dismissKey(iid), String(Date.now())); } catch { /* private mode */ }
  };

  useEffect(() => {
    load();
    // Feb 2026 R281 · Replace bare setInterval + visibilitychange with
    // pollWhenVisible (same catch-up behaviour, less code, and cleanly
    // pauses in background tabs).
    return pollWhenVisible(load, POLL_MS);
  }, [load]);

  const primary = active[0] || null;
  const inLockWindow = primary && (Date.now() - (firstSeenAt.current[primary.instance_id] || Date.now())) < MODAL_LOCK_MS;
  const shouldModal = !!primary && inLockWindow && !dismissedModalIds.has(primary.instance_id);

  // Iter 253z-14 · Snooze-all — check if snooze window is still active.
  const snoozedUntil = (() => {
    try {
      const v = Number(localStorage.getItem(SNOOZE_ALL_KEY));
      return Number.isFinite(v) && v > Date.now() ? v : 0;
    } catch { return 0; }
  })();
  const snoozeActive = snoozedUntil > Date.now();

  // Feb 2026 · Auto-open the Respond dialog when a fresh huddle lands
  // (and the user hasn't dismissed/snoozed/blocked). Removes the
  // "Answer now" indirection that users complained felt slow. The ref
  // guards against reopening after the user closes the dialog.
  useEffect(() => {
    if (!primary) return;
    if (respondingInst) return;
    if (blockedByPolicy || blockedByCheckin) return;
    if (snoozeActive) return;
    if (dismissedModalIds.has(primary.instance_id)) return;
    if (autoOpenedRef.current.has(primary.instance_id)) return;
    autoOpenedRef.current.add(primary.instance_id);
    setRespondingInst(primary);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary?.instance_id, blockedByPolicy, blockedByCheckin, snoozeActive]);

  const snoozeAll = () => {
    const until = Date.now() + SNOOZE_MS;
    try { localStorage.setItem(SNOOZE_ALL_KEY, String(until)); } catch { /* private mode */ }
    // Mark all current huddles as dismissed so they don't re-render.
    setDismissedModalIds((prev) => {
      const next = new Set(prev);
      (active || []).forEach((h) => next.add(h.instance_id));
      return next;
    });
    // Also refresh tick so UI hides immediately.
    setTick((n) => n + 1);
  };

  if (!primary) return null;
  if (snoozeActive) return null;
  // Feb 2026 · Defer the huddle widget while higher-priority modals
  // (policy ack gate, mandatory check-in) own the viewport. Polling
  // continues so the widget pops the moment both blockers clear.
  if (blockedByPolicy || blockedByCheckin) return null;

  // R123 · Feb 2026 — Skip huddle from the reminder itself so users
  // taking a legitimate day off aren't forced to open the full dialog.
  const skipPrimary = async () => {
    if (!window.confirm("Skip this huddle? It will be marked as non-attendance for today.")) return;
    try {
      await api.post(`/huddle-instances/${primary.instance_id}/skip`, { reason: null });
      toast.success("Huddle skipped for today");
      // Dismiss + reload so the widget hides immediately.
      dismissModal(primary.instance_id);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Skip failed";
      toast.error(msg);
    }
  };

  return (
    <>
      {/* Iter 146 P0 — Hide the reminder cards entirely while the Respond
          dialog is open so the modal overlay + inputs are never obstructed
          (previously FloatingCard @ z-70 rendered above shadcn Dialog @ z-50). */}
      {!respondingInst && (
        shouldModal ? (
          <ForcedModal huddle={primary} extraCount={active.length - 1}
                       onCollapse={() => dismissModal(primary.instance_id)}
                       onSnoozeAll={snoozeAll}
                       onSkip={skipPrimary}
                       onAnswer={() => {
                         // Collapse the reminder to a bottom pill before
                         // opening Respond so nothing overlays the dialog.
                         dismissModal(primary.instance_id);
                         setRespondingInst(primary);
                       }} />
        ) : (
          <FloatingCard huddle={primary} extraCount={active.length - 1}
                        collapsed={bodyCollapsed} setCollapsed={setBodyCollapsed}
                        onSnoozeAll={snoozeAll}
                        onSkip={skipPrimary}
                        onAnswer={() => setRespondingInst(primary)} />
        )
      )}
      {respondingInst && (
        <HuddleRespondDialog
          inst={respondingInst}
          onClose={() => setRespondingInst(null)}
          onSubmitted={() => { setRespondingInst(null); load(); }}
        />
      )}
    </>
  );
}


function ForcedModal({ huddle, extraCount, onCollapse, onSnoozeAll, onSkip, onAnswer }) {
  // Iter 174 — allow ESC to collapse (not answer) so power-users and
  // automated flows aren't blocked from other CTAs on the page.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCollapse(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCollapse]);
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center"
         data-testid="huddle-forced-modal">
      <div className="bg-background border-2 border-primary shadow-2xl max-w-md w-[92%] p-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-red-600">Live huddle</span>
          <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTime(huddle.scheduled_at)}
          </span>
        </div>
        <div>
          <h2 className="text-2xl font-black leading-tight">{huddle.huddle_name}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your team is waiting on your update. Takes under a minute.
          </p>
          {extraCount > 0 && (
            <p className="text-[11px] text-amber-700 mt-2">
              + {extraCount} more huddle{extraCount === 1 ? "" : "s"} pending your response.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <Button className="rounded-none flex-1 h-10" onClick={onAnswer} data-testid="huddle-forced-answer">
            <Radio className="w-4 h-4 mr-1" /> Answer now
          </Button>
          <Button
            variant="outline"
            className="rounded-none h-10"
            onClick={onSkip}
            data-testid="huddle-forced-skip"
            title="Mark today as non-attendance for this huddle"
          >
            <SkipForward className="w-3.5 h-3.5 mr-1" /> Skip
          </Button>
        </div>
        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            className="text-[10px] text-muted-foreground hover:text-foreground underline"
            onClick={onCollapse}
            data-testid="huddle-forced-collapse"
            title="Shrinks to a floating card — you still can't fully close it until you submit."
          >
            Not now
          </button>
          {/* Iter 253z-14 · Full 10-min silence across all pending huddles. */}
          <button
            type="button"
            className="text-[10px] text-muted-foreground hover:text-foreground underline"
            onClick={onSnoozeAll}
            data-testid="huddle-snooze-all"
            title="Hide all huddle popups for 10 minutes"
          >
            Snooze 10m
          </button>
        </div>
      </div>
    </div>
  );
}


function FloatingCard({ huddle, extraCount, collapsed, setCollapsed, onSnoozeAll, onSkip, onAnswer }) {
  return (
    <div className="fixed bottom-4 right-4 z-[70] shadow-2xl border-2 border-primary bg-background w-72"
         data-testid="huddle-floating-card">
      <button
        type="button"
        className="w-full bg-primary text-primary-foreground px-3 py-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest"
        onClick={() => setCollapsed(!collapsed)}
        data-testid="huddle-floating-toggle"
      >
        <span className="relative flex h-2 w-2 mr-1">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
        Live huddle
        {extraCount > 0 && <span className="text-[9px] bg-red-500 text-white px-1">+{extraCount}</span>}
        <span className="ml-auto">
          {collapsed ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </span>
      </button>
      {!collapsed && (
        <div className="p-3 space-y-2">
          <div className="text-sm font-bold">{huddle.huddle_name}</div>
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Started {formatTime(huddle.scheduled_at)}
          </div>
          <Button className="rounded-none w-full h-8 text-xs" onClick={onAnswer} data-testid="huddle-floating-answer">
            <Radio className="w-3.5 h-3.5 mr-1" /> Answer huddle
          </Button>
          <Button
            variant="outline"
            className="rounded-none w-full h-7 text-[11px]"
            onClick={onSkip}
            data-testid="huddle-floating-skip"
            title="Mark today as non-attendance for this huddle"
          >
            <SkipForward className="w-3 h-3 mr-1" /> Skip huddle
          </Button>
          <button
            type="button"
            className="w-full text-[10px] text-muted-foreground hover:text-foreground underline text-center pt-0.5"
            onClick={onSnoozeAll}
            data-testid="huddle-floating-snooze"
          >
            Snooze all for 10 min
          </button>
          <p className="text-[9px] text-muted-foreground italic leading-tight">
            This widget stays until you submit your update.
          </p>
        </div>
      )}
    </div>
  );
}


function formatTime(iso) {
  if (!iso) return "just now";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(11, 16); }
}
